import { HOUSEHOLD_MEASUREMENT } from '../../../../../shared/constants/links/linkProperties';
import type { AdLinkFormat, IAdLink, IAdLinkAnalyticsTags, IBaseRedirects } from '../../../../../shared/types';
import { RedirectsClientOverride } from '../../../../../shared/types';
import type { IAdLinkLinkData } from '../../../../../shared/types/links/Interfaces/IAdLinkLinkData';
import { Redirect as RedirectEnum } from '../../screens/ScreenLinkHubAdLinkCreator/steps/RedirectsStep/Redirects.enums';
import type { Redirect as RedirectType } from '../../screens/ScreenLinkHubAdLinkCreator/steps/RedirectsStep/Redirects.types';
import { LinkDataService } from '../LinkDataService';

export class AdLinkEditorService {
  private static getLinkId = (adLink: Record<string, any>): string => {
    return adLink?.id;
  };

  private static getTitle = (adLink: Record<string, any>): string => {
    return adLink?.data?.$link_title;
  };

  private static extractAliasFromUrl(url: string): string | undefined {
    const urlObject = new URL(url);

    const pathSegments = urlObject.pathname.split('/');

    const alias = pathSegments.pop();

    return alias;
  }

  private static getAlias = (adLink: Record<string, any>): string | undefined => {
    if (adLink?.alias) {
      return adLink.alias;
    }

    if (adLink?.url) {
      return this.extractAliasFromUrl(adLink.url);
    }

    return undefined;
  };

  private static getPartner = (adLink: Record<string, any>): string => {
    return adLink?.data?.$3p;
  };

  private static getFormat = (adLink: Record<string, any>): AdLinkFormat => {
    return adLink?.data?.['~branch_ad_format'];
  };

  private static getTemplateId = (adLink: Record<string, any>): AdLinkFormat => {
    return adLink?.data?.['~link_template_id'];
  };

  private static getLinkData = (adLink: Record<string, any>): IAdLinkLinkData & Record<string, string> => {
    const linkDataWithoutPresets: Record<string, any> = LinkDataService.excludePresetsFromLinkCreatorData(
      adLink?.data,
      'ad'
    );

    return linkDataWithoutPresets;
  };

  private static getIsHouseHoldMeasurementEnabled = (adLink: Record<string, any>): boolean => {
    return adLink?.data?.[HOUSEHOLD_MEASUREMENT] || false;
  };

  private static getAnalyticsTags = (adLink: Record<string, any>): IAdLinkAnalyticsTags => {
    const analyticsTags = new Object() as IAdLinkAnalyticsTags;

    if (adLink?.data?.['~channel']) {
      analyticsTags.channel = adLink?.data?.['~channel'];
    }

    if (adLink?.data?.['~campaign']) {
      analyticsTags.campaign = adLink?.data?.['~campaign'];
    }

    if (adLink?.data?.['~tags']) {
      analyticsTags.tags = adLink?.data?.['~tags'];
    }

    return analyticsTags;
  };

  private static getOsType = (adLink: Record<string, any>): string => {
    return adLink?.data?.['~os_type'];
  };

  private static getRedirectValidType = (redirect: RedirectType): RedirectsClientOverride => {
    if (redirect === RedirectEnum.Deepview) {
      return RedirectsClientOverride.Deepview;
    } else if (redirect === RedirectEnum.Web) {
      return RedirectsClientOverride.WebUrl;
    } else {
      return RedirectsClientOverride.DefaultRedirect;
    }
  };

  private static getRedirects = (adLink: Record<string, any>): IBaseRedirects => {
    const redirects: IBaseRedirects = new Object();

    if (adLink?.data?.$ios_url) {
      redirects.ios = {
        override: this.getRedirectValidType('web'),
        value: adLink?.data?.$ios_url,
      };
    } else if (adLink?.data?.$ios_deepview) {
      redirects.ios = {
        override: this.getRedirectValidType('deepview'),
        value: adLink?.data?.$ios_deepview,
      };
    }

    if (adLink?.data?.$android_url) {
      redirects.android = {
        override: this.getRedirectValidType('web'),
        value: adLink?.data?.$android_url,
      };
    } else if (adLink?.data?.$android_deepview) {
      redirects.android = {
        override: this.getRedirectValidType('deepview'),
        value: adLink?.data?.$android_deepview,
      };
    }

    if (adLink?.data?.$desktop_url) {
      redirects.desktop = {
        override: this.getRedirectValidType('web'),
        value: adLink?.data?.$desktop_url,
      };
    } else if (adLink?.data?.$desktop_deepview) {
      redirects.desktop = {
        override: this.getRedirectValidType('deepview'),
        value: adLink?.data?.$desktop_deepview,
      };
    }

    return redirects;
  };

  public static getAdLinkPayload = (adLink: Record<string, any>): IAdLink => {
    return {
      id: this.getLinkId(adLink),
      title: this.getTitle(adLink),
      alias: this.getAlias(adLink),
      partner: this.getPartner(adLink),
      format: this.getFormat(adLink),
      linkData: this.getLinkData(adLink),
      analyticsTags: this.getAnalyticsTags(adLink),
      osType: this.getOsType(adLink),
      redirects: this.getRedirects(adLink),
      templateId: this.getTemplateId(adLink),
      isHouseholdMeasurementEnabled: this.getIsHouseHoldMeasurementEnabled(adLink),
    };
  };
}
